import React, { useEffect } from 'react';
import { useAuth } from 'oidc-react';
import { setTokenGetter } from '../../components/services/apiService';
import logo from '../../images/logo.png';
import IdleSessionManager from '../IdleSessionManager';

interface AuthGateProps {
  children: React.ReactNode;
}

const LoadingScreen = () => {
  return (
    <div className="fixed inset-0 flex items-center justify-center">
      <div className="relative">
        {/* Animated circular progress */}
        <svg className="w-32 h-32 -rotate-90" viewBox="0 0 120 120">
          <circle cx="60" cy="60" r="54" fill="none" stroke="#2a2a2b" strokeWidth="4" />
          <circle
            cx="60"
            cy="60"
            r="54"
            fill="none"
            stroke="#dc2626"
            strokeWidth="4"
            strokeDasharray="339.292"
            strokeDashoffset="339.292"
            strokeLinecap="round"
            className="animate-spin-progress"
          />
        </svg>

        {/* Logo in center */}
        <div className="absolute inset-0 flex items-center justify-center">
          <img src={logo} alt="Loading" className="w-16 h-16 object-contain animate-pulse" />
        </div>
      </div>

      {/* Loading text */}
      <div className="absolute mt-48">
        <div className="flex items-center gap-2">
          <span className="text-gray-400 text-sm font-medium">Loading</span>
          <div className="flex gap-1">
            <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
            <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
            <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin-progress {
          0% {
            stroke-dashoffset: 339.292;
            transform: rotate(0deg);
          }
          50% {
            stroke-dashoffset: 84.823;
            transform: rotate(180deg);
          }
          100% {
            stroke-dashoffset: 339.292;
            transform: rotate(360deg);
          }
        }
        
        .animate-spin-progress {
          animation: spin-progress 2s cubic-bezier(0.4, 0, 0.2, 1) infinite;
          transform-origin: center;
        }
      `}</style>
    </div>
  );
};

const AuthGate = ({ children }: AuthGateProps) => {
  const auth = useAuth();

  useEffect(() => {
    setTokenGetter(() => auth?.userData?.access_token);
  }, [auth?.userData?.access_token]);

  useEffect(() => {
    if (!auth.userData) {
      const path = window.location.pathname;
      if (path.startsWith('/patchCenter/ApproveFromEmail/')) {
        const planId = path.split('/').pop();
        if (planId) {
          sessionStorage.setItem('pendingApprovalPlanId', planId);
        }
      }
      return;
    }

    const pendingApprovalPlanId = sessionStorage.getItem('pendingApprovalPlanId');
    const path = window.location.pathname;

    if (pendingApprovalPlanId && (path === '/' || path === '/customers')) {
      const redirectTimer = window.setTimeout(() => {
        window.location.href = `/patchCenter/ApproveFromEmail/${pendingApprovalPlanId}`;
      }, 250);

      return () => window.clearTimeout(redirectTimer);
    }
  }, [auth.userData]);

  if (auth.isLoading || typeof auth.userData === 'undefined') {
    return <LoadingScreen />;
  }

  if (!auth.userData) {
    // User is not logged in yet, oidc-react will handle redirect
    return null;
  }

  return (
    <>
      {auth.userManager && <IdleSessionManager />}
      {children}
    </>
  );
};

export default AuthGate;
